import type { Client, UserGroup } from '@efdigital/ll-addon-data';
import { DataTypeSystem } from '@efdigital/ll-addon-data/data-types';
import { setAsyncBelongsTo } from '@efdigital/ll-admin-v2/data/async-setter.ts';
import {
  dummyUserAdmin,
  dummyUserParticipant,
} from '@efdigital/ll-admin-v2/utils/user-groups';
import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type { Store } from '@warp-drive/core';

import type { ProgramsProgramCohortModel } from '../cohort.ts';

export type ProgramsProgramCohortParticipantsModel =
  ProgramsProgramCohortModel & {
    group: UserGroup;
    admingroup: UserGroup;
    client: Client | null;
  };

export default class ProgramsProgramCohortParticipantsRoute extends Route<ProgramsProgramCohortParticipantsModel> {
  @service declare store: Store;

  async model(): Promise<ProgramsProgramCohortParticipantsModel> {
    const parentModel = this.modelFor(
      'programs.program.cohort'
    ) as ProgramsProgramCohortModel;
    const cohort = parentModel.cohort;
    const cohortClient = await cohort.client;

    let group = await cohort.group;
    if (!group) {
      group = this.store.createRecord<UserGroup>(DataTypeSystem.UserGroup, {
        title: `${cohort.slug}-participants`,
        userIds: [dummyUserParticipant],
        scope: 'va',
        category: 'Xceed',
        expiresAfter: 0,
        ...(cohortClient ? { client: cohortClient } : {}),
      });
      setAsyncBelongsTo(cohort, 'group', group);
    } else {
      // Backfill client on groups created before this field was set. Must await
      // to resolve the PromiseBelongsTo proxy — the proxy itself is always
      // truthy even when the underlying value is null.
      const groupClient =
        await (group.client as unknown as Promise<Client | null>);
      if (!groupClient && cohortClient) {
        group.client = cohortClient;
      }
    }

    let admingroup = await cohort.admingroup;
    if (!admingroup) {
      admingroup = this.store.createRecord<UserGroup>(
        DataTypeSystem.UserGroup,
        {
          title: `${cohort.slug}-admins`,
          userIds: [dummyUserAdmin],
          scope: 'va',
          category: 'Xceed',
          expiresAfter: 0,
          ...(cohortClient ? { client: cohortClient } : {}),
        }
      );
      setAsyncBelongsTo(cohort, 'admingroup', admingroup);
    } else {
      const admingroupClient =
        await (admingroup.client as unknown as Promise<Client | null>);
      if (!admingroupClient && cohortClient) {
        admingroup.client = cohortClient;
      }
    }

    return {
      ...parentModel,
      group,
      admingroup,
      client: cohortClient,
    };
  }
}
